import { z } from 'zod';

export type OrderStatus =
  | 'created'
  | 'cancelled'
  | 'completed'
  | 'drop-off-confirmed'
  | 'drop-off-scheduled'
  | 'in-transit-drop-off'
  | 'in-transit-pickup'
  | 'payment-failed'
  | 'payment-pending'
  | 'pending-drop-off'
  | 'pending-pickup'
  | 'pickup-confirmed'
  | 'pickup-requested'
  | 'pickup-scheduled'
  | 'restricted'
  | 'refunded'
  | 'refund-failed'
  | 'waste-processing';

export enum OrderStatusEnum {
  Created = 'created',
  Cancelled = 'cancelled',
  Completed = 'completed',
  DropOffConfirmed = 'drop-off-confirmed',
  DropOffScheduled = 'drop-off-scheduled',
  InTransitDropOff = 'in-transit-drop-off',
  InTransitPickup = 'in-transit-pickup',
  PaymentFailed = 'payment-failed',
  PaymentPending = 'payment-pending',
  PendingDropOff = 'pending-drop-off',
  PendingPickup = 'pending-pickup',
  PickupConfirmed = 'pickup-confirmed',
  PickupRequested = 'pickup-requested',
  PickupScheduled = 'pickup-scheduled',
  Restricted = 'restricted',
  Refunded = 'refunded',
  RefundFailed = 'refund-failed',
  WasteProcessing = 'waste-processing',
}

export enum OrderAction {
  Pickup = 'pickup',
  DropOff = 'drop-off',
}

export interface AdditionalLineItem {
  label: string;
  amount: number;
  postTax: boolean;
}

/**
 * Line items finalized after waste processing is complete
 * Contains actual costs vs quoted costs for weight, containers, and materials
 */
export interface FinalizedOrderLineItems {
  weight: {
    tons: number;
    totalCostInCents: number;
    adjustCostInCents: number;
  };
  containersAdjustedCostInCents: number;
  additionalMaterialChargesAdjustedCostInCents: number;
  containers: {
    containerId: string;
    days: number;
    totalCostInCents: number;
    // Detailed breakdown for admin display (optional for backward compatibility)
    includedDays?: number;
    extraDaysPaid?: number; // Days prepaid beyond included at checkout
    refundDays?: number; // Days being refunded
    refundAmountInCents?: number;
    additionalDays?: number; // Days charged beyond prepaid
    additionalCostInCents?: number;
  }[];
  additionalMaterialCharges: {
    materialId: string;
    quantity: number;
    totalCostInCents: number;
  }[];
  percentageDiscount?: number;
  flatDiscountInCents?: number;
}

export const orderActionZ = z.enum([OrderAction.Pickup, OrderAction.DropOff]);

/**
 * Safely validates if a value is a valid OrderStatusEnum
 * @param status - The status value to validate
 * @returns The status as OrderStatusEnum if valid, or null if invalid
 */
export const isValidOrderStatus = (status: unknown): status is OrderStatusEnum => {
  return (
    typeof status === 'string' && Object.values(OrderStatusEnum).includes(status as OrderStatusEnum)
  );
};

/**
 * Safely converts a value to OrderStatusEnum, throwing an error if invalid
 * @param status - The status value to convert
 * @returns The status as OrderStatusEnum
 * @throws Error if status is not a valid OrderStatusEnum
 */
export const toOrderStatusEnum = (status: unknown): OrderStatusEnum => {
  if (isValidOrderStatus(status)) {
    return status;
  }
  throw new Error(`Invalid order status: ${status}`);
};

/**
 * The operational lifecycle of an order, ordered from first to last stage.
 *
 * Statuses grouped into the same stage are operationally interchangeable (a
 * "pending" hold is the same point in the flow as its scheduled counterpart),
 * so moving between them is neither forward nor backward progress.
 *
 * Statuses that are NOT in this list (cancelled, refunded, restricted and the
 * payment-* / refund-failed exception states) sit outside the linear flow and
 * are intentionally excluded — see `validateManualOrderStatusTransition`.
 */
export const ORDER_LIFECYCLE_STAGES: readonly (readonly OrderStatusEnum[])[] = [
  [OrderStatusEnum.Created],
  [OrderStatusEnum.DropOffScheduled, OrderStatusEnum.PendingDropOff],
  [OrderStatusEnum.InTransitDropOff],
  [OrderStatusEnum.DropOffConfirmed],
  [OrderStatusEnum.PickupRequested],
  [OrderStatusEnum.PickupScheduled, OrderStatusEnum.PendingPickup],
  [OrderStatusEnum.InTransitPickup],
  [OrderStatusEnum.PickupConfirmed],
  [OrderStatusEnum.WasteProcessing],
  [OrderStatusEnum.Completed],
];

/**
 * Index of a status within ORDER_LIFECYCLE_STAGES, or -1 when the status sits
 * outside the linear lifecycle (cancelled, refunded, restricted, payment-*).
 */
export const getOrderLifecycleStageIndex = (status: OrderStatusEnum): number =>
  ORDER_LIFECYCLE_STAGES.findIndex((stage) => stage.includes(status));

/**
 * Operational data that must exist on the order before it may move forward
 * past a given lifecycle stage. Keyed by the stage index the field is produced
 * at: you cannot advance beyond stage N without the field stage N stamps.
 */
const REQUIRED_LIFECYCLE_DATA: ReadonlyArray<{
  status: OrderStatusEnum;
  field: 'deliveryConfirmedAt' | 'pickupConfirmedAt';
}> = [
  { status: OrderStatusEnum.DropOffConfirmed, field: 'deliveryConfirmedAt' },
  { status: OrderStatusEnum.PickupConfirmed, field: 'pickupConfirmedAt' },
];

/**
 * Structural shape of one `statusHistory` entry: a single-key object mapping the
 * status the order entered to the metadata recorded at that point. Declared
 * structurally so `@libs/schemas` stays free of a `@libs/models` dependency.
 */
export type ManualStatusHistoryEntry = Partial<Record<OrderStatusEnum, unknown>>;

export interface ManualStatusTransitionOrderData {
  deliveryConfirmedAt?: Date | string | null;
  pickupConfirmedAt?: Date | string | null;
  statusHistory?: readonly ManualStatusHistoryEntry[] | null;
}

export interface ManualStatusTransitionResult {
  isValid: boolean;
  error?: string;
}

/**
 * The furthest lifecycle stage the order has ever actually reached, read from
 * `statusHistory`, or -1 when the history is unavailable or contains no
 * lifecycle status.
 *
 * This is what anchors an order that is currently parked on an exception status
 * (`restricted`, `cancelled`, `payment-failed`, ...). Those statuses have no
 * position in the linear flow, so without this the order would appear to have
 * no lifecycle progress at all and any forward move would look legitimate.
 *
 * "Furthest reached" rather than "most recent" is deliberate: an order that
 * genuinely completed drop-off and was later rolled back still has its
 * `deliveryConfirmedAt` stamped, so replaying it forward is a real correction,
 * not a fabricated progression.
 */
export const getFurthestOrderLifecycleStageIndex = (
  statusHistory: readonly ManualStatusHistoryEntry[] | null | undefined,
): number => {
  if (!statusHistory?.length) {
    return -1;
  }

  let furthest = -1;
  for (const entry of statusHistory) {
    if (!entry) continue;
    for (const key of Object.keys(entry)) {
      if (!isValidOrderStatus(key)) continue;
      const index = getOrderLifecycleStageIndex(key);
      if (index > furthest) {
        furthest = index;
      }
    }
  }

  return furthest;
};

/**
 * Guardrail for the admin "manual status change" path, which deliberately
 * bypasses VALID_STATUS_TRANSITIONS (and notifications) so ops can correct
 * bad data.
 *
 * Bypassing transition validation entirely let admins jump an order *forward*
 * over required operational states — e.g. `in-transit-drop-off ->
 * pickup-requested`, which leaves `deliveryConfirmedAt` unset while the status
 * history still reads as a legitimate progression. This validator keeps the
 * escape hatch useful while making those skips impossible.
 *
 * The explicit rules:
 *  1. Moving *out* of the lifecycle onto an exception status (cancelled,
 *     refunded, restricted, payment-*, refund-failed) is ALLOWED from anywhere.
 *     These are genuine admin corrections and imply no operational work.
 *  2. Moving to `completed` is ALLOWED from anywhere. It is the terminal stage
 *     and consumes none of the data the earlier stages produce (no container,
 *     no scheduling dates), and this path takes no payment and sends no
 *     notification, so closing an order out early moves no money and tells the
 *     customer nothing. Ops needs it for refunds and similar goodwill cases
 *     where the order simply has to be closed rather than walked through every
 *     intermediate step.
 *  3. Moving *into* any other lifecycle stage is measured from the furthest
 *     stage the order actually reached, not from its current status. For an
 *     order sitting on a lifecycle status that is the status itself; for one
 *     parked on an exception status it is recovered from `statusHistory`.
 *     Without this, an exception status is a backdoor: `drop-off-scheduled ->
 *     restricted -> pickup-confirmed` would satisfy a naive per-hop check while
 *     skipping the whole flow.
 *  4. Backward or same-stage moves are ALLOWED — this is the primary reason
 *     the manual path exists (rescheduling, undoing a mis-click, recovering an
 *     order from `restricted` back to where it was).
 *  5. Forward moves are ALLOWED only to the immediately next lifecycle stage.
 *     Skipping one or more stages is BLOCKED.
 *  6. A forward move is additionally BLOCKED when the order is missing the
 *     operational data an already-passed stage was supposed to stamp
 *     (`deliveryConfirmedAt`, `pickupConfirmedAt`). This one is enforced even
 *     when the anchor is unknown, because it is the invariant that actually
 *     matters: an order must never look operationally complete without it.
 */
export const validateManualOrderStatusTransition = (
  currentStatus: OrderStatusEnum,
  newStatus: OrderStatusEnum,
  order: ManualStatusTransitionOrderData = {},
): ManualStatusTransitionResult => {
  if (currentStatus === newStatus) {
    return { isValid: true };
  }

  const currentIndex = getOrderLifecycleStageIndex(currentStatus);
  const newIndex = getOrderLifecycleStageIndex(newStatus);

  // Rule 1 — leaving the lifecycle (or moving between exception statuses) is
  // always allowed. It claims no operational progress, so nothing to enforce.
  if (newIndex === -1) {
    return { isValid: true };
  }

  // Rule 2 — closing an order out is always allowed. `completed` is terminal and
  // needs nothing the skipped stages would have produced, so it is exempt from
  // both the skip rule and the required-data rule below. Deliberately placed
  // ahead of them: the required-data rule would otherwise still reject it for an
  // unstamped deliveryConfirmedAt.
  if (newStatus === OrderStatusEnum.Completed) {
    return { isValid: true };
  }

  // Rule 3 — anchor the move to real progress, not to the current status.
  const anchorIndex =
    currentIndex !== -1 ? currentIndex : getFurthestOrderLifecycleStageIndex(order.statusHistory);
  // When re-entering from an exception status, name the stage we measured from —
  // "from restricted" alone would not explain why the move was rejected.
  const origin =
    currentIndex !== -1 || anchorIndex === -1
      ? currentStatus
      : `${currentStatus} (last reached ${ORDER_LIFECYCLE_STAGES[anchorIndex].join(' or ')})`;

  // Rule 4 — backward or lateral moves stay allowed.
  if (anchorIndex !== -1 && newIndex <= anchorIndex) {
    return { isValid: true };
  }

  // Rule 5 — forward moves may not skip stages.
  if (anchorIndex !== -1 && newIndex > anchorIndex + 1) {
    const skipped = ORDER_LIFECYCLE_STAGES.slice(anchorIndex + 1, newIndex)
      .map((stage) => stage.join(' or '))
      .join(', ');
    return {
      isValid: false,
      error:
        `Cannot manually move order forward from ${origin} to ${newStatus}: ` +
        `it would skip ${skipped}. Advance the order one stage at a time so the ` +
        `required operational data is recorded.`,
    };
  }

  // Rule 6 — forward moves require the data earlier stages should have stamped.
  for (const { status, field } of REQUIRED_LIFECYCLE_DATA) {
    const requiredAtIndex = getOrderLifecycleStageIndex(status);
    // Only enforce for stages the order is moving *past*, not the one it is entering.
    if (requiredAtIndex < newIndex && !order[field]) {
      return {
        isValid: false,
        error:
          `Cannot manually move order forward from ${origin} to ${newStatus}: ` +
          `${field} is not set. Set the order to ${status} first so ${field} is recorded.`,
      };
    }
  }

  return { isValid: true };
};

// Payment schemas
export const createPaymentSchema = z
  .object({
    paymentType: z.enum(['card', 'non-card', 'existing-card']),
    amountInCents: z.number().min(1, 'Amount must be greater than $0'),
    description: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.paymentType === 'non-card') {
        return data.description && data.description.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Description is required for external payments',
      path: ['description'],
    },
  );

export const adminCreateExternalPaymentSchema = z.object({
  orderId: z.string(),
  amountInCents: z.number().min(1, 'Amount must be greater than $0'),
  description: z.string().min(1, 'Description is required for external payments'),
});

export const adminChargeExistingPaymentMethodSchema = z.object({
  orderId: z.string(),
  amountInCents: z.number().min(1, 'Amount must be greater than $0'),
  paymentMethodId: z.string().min(1, 'Payment method ID is required'),
});

export type CreatePaymentForm = z.infer<typeof createPaymentSchema>;
export type AdminCreateExternalPaymentInput = z.infer<typeof adminCreateExternalPaymentSchema>;
export type AdminChargeExistingPaymentMethodInput = z.infer<
  typeof adminChargeExistingPaymentMethodSchema
>;
